import { useEffect, useState, useCallback, useRef } from "react";
import { sfx } from "../../lib/soundEngine";

const START_WINDOW = 1400; // ms allowed to hit the pad initially
const MIN_WINDOW = 380;
const DECAY = 45; // ms shaved per successful hit

export const NeonReflexGame = () => {
    const [running, setRunning] = useState(false);
    const [activePad, setActivePad] = useState(-1);
    const [activeColor, setActiveColor] = useState("amber");
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);
    const [streak, setStreak] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const windowRef = useRef(START_WINDOW);
    const timerRef = useRef(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const spawn = useCallback(() => {
        const pad = Math.floor(Math.random() * 9);
        const color = Math.random() < 0.5 ? "amber" : "pink";
        setActivePad(pad);
        setActiveColor(color);
        sfx.tick();
        timerRef.current = setTimeout(() => {
            // timed out → game over
            setActivePad(-1);
            setRunning(false);
            setGameOver(true);
            sfx.gameOver();
            setBest((prev) => (score > prev ? score : prev));
        }, windowRef.current);
    }, [score]);

    const start = useCallback(() => {
        clearTimer();
        setScore(0);
        setStreak(0);
        setGameOver(false);
        windowRef.current = START_WINDOW;
        setRunning(true);
        sfx.coin();
    }, []);

    useEffect(() => {
        if (running && activePad === -1 && !gameOver) {
            const t = setTimeout(spawn, 180);
            return () => clearTimeout(t);
        }
    }, [running, activePad, gameOver, spawn]);

    useEffect(() => () => clearTimer(), []);

    const hit = (i) => {
        if (!running) return;
        if (i !== activePad) {
            // wrong pad → penalty end
            clearTimer();
            setRunning(false);
            setGameOver(true);
            sfx.fail();
            setActivePad(-1);
            setBest((prev) => (score > prev ? score : prev));
            return;
        }
        clearTimer();
        sfx.eat();
        setScore((s) => s + 1);
        setStreak((s) => s + 1);
        windowRef.current = Math.max(MIN_WINDOW, windowRef.current - DECAY);
        setActivePad(-1);
    };

    const windowPct = Math.round(
        ((windowRef.current - MIN_WINDOW) / (START_WINDOW - MIN_WINDOW)) * 100
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between font-pixel text-lg">
                <span className="neon-amber" data-testid="reflex-score">SCORE: {score}</span>
                <span className="neon-pink" data-testid="reflex-streak">STREAK: {streak}</span>
                <span className="neon-cyan" data-testid="reflex-best">BEST: {best}</span>
            </div>

            <div className="relative grid grid-cols-3 gap-2 mx-auto" style={{ width: "100%", maxWidth: 320 }}>
                {Array.from({ length: 9 }).map((_, i) => (
                    <button
                        key={i}
                        data-testid={`reflex-pad-${i}`}
                        onClick={() => hit(i)}
                        className={`reflex-pad ${
                            activePad === i
                                ? activeColor === "amber"
                                    ? "active-amber"
                                    : "active-pink"
                                : ""
                        }`}
                        aria-label={`Reflex pad ${i}`}
                    />
                ))}

                {gameOver && (
                    <div
                        data-testid="reflex-gameover-modal"
                        className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 rounded-md border-2"
                        style={{ borderColor: "var(--neon-pink)" }}
                    >
                        <p className="font-pixel text-3xl neon-pink">GAME OVER</p>
                        <p className="font-pixel text-lg neon-amber mt-1">Final Score: {score}</p>
                        <p className="font-pixel text-sm text-gray-400 mt-1">Longest Streak: {streak}</p>
                        <button onClick={start} data-testid="reflex-restart-btn" className="btn-neon btn-neon-pink mt-4">
                            <i className="fa-solid fa-play" /> Restart
                        </button>
                    </div>
                )}

                {!running && !gameOver && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-md">
                        <button onClick={start} data-testid="reflex-start-btn" className="btn-neon btn-neon-amber">
                            <i className="fa-solid fa-bolt" /> Start
                        </button>
                    </div>
                )}
            </div>

            <div className="w-full h-2 rounded-full overflow-hidden bg-white/5 mt-2" data-testid="reflex-window-bar">
                <div
                    className="h-full transition-all"
                    style={{
                        width: `${Math.max(6, windowPct)}%`,
                        background: "linear-gradient(90deg, var(--neon-amber), var(--neon-pink))",
                        boxShadow: "0 0 10px var(--neon-pink)",
                    }}
                />
            </div>
            <p className="text-xs text-gray-500 text-center font-body">
                Tap the glowing pad before it goes dark. Windows shrink as you level up.
            </p>
        </div>
    );
};
